/**
 * The entities of the space on screen, loaded once (by the shell; from outside, by the item's page), so an icon or a
 * name anywhere (a link to an item in a text) can find them without asking for them again.
 */
import { signal } from "@preact/signals";
import { useEffect } from "preact/hooks";
import type { ObjEntity, SpaceOut } from "../api/types.gen.ts";
import { useEntities } from "./data.ts";

/** The entities, with the space they're of: another space's never stand in for this one's. */
const loaded = signal<{ space: string; entities: readonly ObjEntity[] } | null>(null);

/** Keeps the entities of `space` loaded (and fresh, on live updates). */
export function useEntitiesLoader(space: SpaceOut): void {
  const r = useEntities(space);
  useEffect(() => {
    if (r.data) {
      loaded.value = { space: space.id, entities: r.data };
    }
  }, [r.data, space.id]);
}

/** Entity `id` of `space`, if loaded. */
export function entityOf(space: Pick<SpaceOut, "id">, id: string | null | undefined): ObjEntity | undefined {
  const l = loaded.value;
  return id && l?.space === space.id ? l.entities.find((e) => e.id === id) : undefined;
}

/** The entities of `space`, if loaded. */
export function entitiesOf(space: Pick<SpaceOut, "id">): readonly ObjEntity[] | undefined {
  const l = loaded.value;
  return l?.space === space.id ? l.entities : undefined;
}
